import { CommonModule } from '@angular/common';
import { Component, Inject, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatIcon } from '@angular/material/icon';
import {
  CreateAssetMetadataInput,
  JsAssetMetadata,
} from '../../_interfaces/others.interface';

@Component({
  selector: 'app-asset-upload-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DragDropModule,
    MatFormField,
    MatInput,
    MatIcon,
  ],
  templateUrl: './asset-upload-dialog.component.html',
  styleUrl: './asset-upload-dialog.component.scss',
})
export class AssetUploadDialogComponent {
  @ViewChild('assetForm') assetForm!: NgForm;
  metadataForm: FormGroup;
  tagInput: string = '';
  isEdit: boolean = false;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AssetUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AssetUploadDialogModel
  ) {
    this.isEdit = data.isEdit || false;

    // Pre-populate form based on edit mode or new upload
    let defaultName = '';
    let defaultDescription = '';
    let defaultTags: string[] = [];
    let defaultAltText = '';

    if (this.isEdit && data.asset) {
      defaultName = data.asset.displayName;
      defaultDescription = data.asset.description || '';
      defaultTags = data.asset.tags || [];
      defaultAltText = data.asset.altText || '';
    } else {
      defaultName = this.getNameWithoutExtension(data.file.name);
    }

    this.metadataForm = this.fb.group({
      displayName: new FormControl(defaultName, [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(100),
      ]),
      description: new FormControl(defaultDescription, Validators.maxLength(500)),
      tags: new FormControl<string[]>(defaultTags),
      altText: new FormControl(defaultAltText, Validators.maxLength(200)),
    });
  }

  getNameWithoutExtension(filename: string): string {
    const lastDot = filename.lastIndexOf('.');
    return lastDot > 0 ? filename.substring(0, lastDot) : filename;
  }

  get tags(): string[] {
    return this.metadataForm.get('tags')?.value || [];
  }

  addTag() {
    const tag = this.tagInput.trim().toLowerCase();
    if (tag && !this.tags.includes(tag)) {
      this.metadataForm.patchValue({ tags: [...this.tags, tag] });
    }
    this.tagInput = '';
  }

  removeTag(tag: string) {
    this.metadataForm.patchValue({
      tags: this.tags.filter((t) => t !== tag),
    });
  }

  onTagKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.addTag();
    }
  }

  close() {
    this.dialogRef.close(null);
  }

  submit() {
    if (this.metadataForm.invalid) return;

    const metadata: CreateAssetMetadataInput = {
      displayName: this.metadataForm.value.displayName.trim(),
      description: this.metadataForm.value.description?.trim() || '',
      tags: this.metadataForm.value.tags || [],
      altText: this.metadataForm.value.altText?.trim() || '',
    };

    if (this.isEdit) {
      // Edit mode - only return metadata
      this.dialogRef.close({ metadata });
    } else {
      // Upload mode - return file and metadata
      this.dialogRef.close({
        file: this.data.file,
        metadata,
      });
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }
}

export class AssetUploadDialogModel {
  constructor(
    public file: File | { name: string; size: number },
    public isEdit: boolean = false,
    public asset?: JsAssetMetadata
  ) {}
}
